import { create } from 'zustand';
import type { SkillInfo, ChatModel, AgentProfile, CollabStatusEntry, ProcessLogEntry } from '@/types/agent';
import type { Message, AttachmentItem, ChatHistoryEntry, BackgroundTask } from '@/components/agent/types';
import { AgentService } from '@/services/agent/AgentService';
import { unifiedStorage } from '@/services/unifiedStorage';
import { initCrossTabSync, notifyOtherTabs } from '@/services/crossTabSync';
import { createSetter } from './utils/create-setter';
import { thinkingTimerMap, thinkingSecondsMap } from '@/components/agent/streamState';

interface AgentState {
  messages: Message[];
  currentModelId: string;
  sessionId: string | null;
  projectId: string | null;
  skills: SkillInfo[];
  abortController: AbortController | null;
  availableModels: ChatModel[];
  searchMode: 'auto' | 'fast' | 'deep';
  agents: AgentProfile[];
  currentAgentId: string | null;
  defaultAgentId: string | null;
  dispatcherDegraded: boolean;
  degradationLevel: number;
  degradationMessage: string;
  backgroundTasks: BackgroundTask[];
  activeCollabStatuses: CollabStatusEntry[];

  isLoading: boolean;
  isThinking: boolean;
  thinkingSeconds: number;
  currentIntent: string;
  input: string;
  streamingText: string | null;
  activeAssistantIntro: string | null;
  activeProcessLogs: ProcessLogEntry[];
  attachments: AttachmentItem[];
  pendingBackendMsgIds: { user_msg_id?: string; assistant_msg_id?: string } | null;
  pendingCanvasNodeId: string | null;
  // 🔴 P0-2: 最后一次收到 SSE chunk 的时间戳，用于 polling 判断 SSE 是否仍活跃
  lastChunkReceivedAt: number | null;
  // 🔴 F-1: finalizeAssistantMessage 原子性标志（P2-2 新增，但未在接口声明）
  isFinalizing: boolean;
  // 🔵 流式会话结束标志，防止 abort 后回调重新初始化 builder
  streamEnded: boolean;
  // 🔵 #21: pendingLocalMsgIds 已移除，builder 架构下消息 ID 统一在 builder.id 中维护
  chatHistory: ChatHistoryEntry[];
  selectedHistoryId: string | null;
  showHistoryModal: boolean;

  // 🔵 P2: ActiveReplyBuilder - 统一的助理回复构建器
  // ⚠️ P1-5: 已移除 searchResultReceived / generationConfirmReceived 标志位
  // 所有"是否为搜索/生成确认场景"的判断统一使用 activeReplyBuilder.type === 'search' / 'generation_confirm'
  // builder 为 null 表示当前不在流式回复中；builder.type 决定场景类型

  // 在流式会话中，所有事件回调（文本 chunk / 搜索结果 / 生成确认等）都只更新此构建器
  // 完成时由 handleComplete 从 builder 生成最终消息 → 彻底消除"多条消息同时被追加"的竞态
  activeReplyBuilder: {
    id: string | null;           // 临时或后端消息 ID
    type: string | null;          // 'text' | 'search' | 'generation_confirm' | ...
    introText: string | null;     // 辅助性引言（如"正在为您搜索..."）
    text: string | null;          // 文本内容（由 chunk 累积）
    searchData: any | null;       // 搜索结果数据（由 onSearchResult 设置）
    generationOptions: any | null; // 生成确认参数（由 onGenerationConfirm 设置）
    mediaData: any | null;        // 媒体数据
    metadata: Record<string, any>; // 扩展字段
    warning?: string;             // 🔴 P2-方案4: 搜索场景下的错误/警告文本（不追加到 streamingText）
    pendingMarkdown?: string;      // 🔴 P0(v4): markdown 结构缓冲区，暂存可能被 SSE chunk 分割的尾部
  } | null;

  // 🔵 ActiveReplyBuilder 操作方法
  initActiveReplyBuilder: () => void;
  updateActiveReplyBuilder: (patch: Partial<NonNullable<AgentState['activeReplyBuilder']>>) => void;
  clearActiveReplyBuilder: () => void;

  setCurrentModel: (modelId: string) => void;
  setSessionId: (sessionId: string | null) => void;
  setProjectId: (projectId: string | null) => void;
  setSkills: (skills: SkillInfo[]) => void;
  setAbortController: (controller: AbortController | null) => void;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  setAvailableModels: (models: ChatModel[]) => void;
  setSearchMode: (mode: 'auto' | 'fast' | 'deep') => void;
  setCurrentAgentId: (agentId: string | null) => void;
  loadAgents: () => Promise<void>;
  ensureSession: (projectId: string) => Promise<string>;
  restoreSession: (sessionId: string) => Promise<void>;
  closeSession: () => Promise<void>;
  resetSession: () => void;
  initializeSession: (projectId: string) => Promise<void>;
  saveToLocal: () => Promise<void>;
  setDegradationStatus: (degraded: boolean, level?: number, message?: string) => void;
  setActiveCollabStatuses: (value: CollabStatusEntry[] | ((prev: CollabStatusEntry[]) => CollabStatusEntry[])) => void;
  clearActiveCollabStatuses: () => void;
  setBackgroundTasks: (tasks: BackgroundTask[]) => void;

  setIsLoading: (loading: boolean) => void;
  setIsThinking: (thinking: boolean) => void;
  setThinkingSeconds: (seconds: number | ((prev: number) => number)) => void;
  setCurrentIntent: (intent: string) => void;
  setInput: (input: string | ((prev: string) => string)) => void;
  setStreamingText: (text: string | null | ((prev: string | null) => string | null)) => void;
  setActiveAssistantIntro: (intro: string | null) => void;
  setActiveProcessLogs: (logs: ProcessLogEntry[] | ((prev: ProcessLogEntry[]) => ProcessLogEntry[])) => void;
  setAttachments: (attachments: AttachmentItem[] | ((prev: AttachmentItem[]) => AttachmentItem[])) => void;
  setPendingBackendMsgIds: (ids: { user_msg_id?: string; assistant_msg_id?: string } | null) => void;
  setPendingCanvasNodeId: (id: string | null) => void;
  setChatHistory: (history: ChatHistoryEntry[] | ((prev: ChatHistoryEntry[]) => ChatHistoryEntry[])) => void;
  setSelectedHistoryId: (id: string | null) => void;
  setShowHistoryModal: (show: boolean | ((prev: boolean) => boolean)) => void;
  resetActiveReplyState: () => void;
  resetStreamingState: () => void;
  updateActiveProcessLogs: (updater: (prev: ProcessLogEntry[]) => ProcessLogEntry[]) => void;
  // 🔴 P2-1: activeProcessLogs 受控操作方法（禁止直接 updateActiveProcessLogs）
  // - appendActiveProcessLog: 追加单条日志（内部去重：相同 title + kind 不重复添加）
  // - completeLatestActiveProcessLog: 将最近的 active 状态 log 标记为 completed
  // - failLatestActiveProcessLog: 将最近的 active 状态 log 标记为 error
  appendActiveProcessLog: (log: ProcessLogEntry) => void;
  completeLatestActiveProcessLog: (title?: string, detail?: string) => void;
  failLatestActiveProcessLog: (title?: string, detail?: string) => void;
  clearThinkingTimer: () => void;
  startThinkingTimer: () => void;
  // 🔴 P0-2: SSE chunk 到达时标记时间戳，供 polling 判断 SSE 是否仍活跃
  bumpChunkReceivedAt: () => void;
  // ⚠️ P1-5: setGenerationConfirmReceived / setSearchResultReceived 已移除
  // 请使用 updateActiveReplyBuilder({ type: 'search' | 'generation_confirm' }) 替代
}

initCrossTabSync((sessionId, messages) => {
  const state = useAgentStore.getState();
  if (state.sessionId === sessionId) {
    useAgentStore.setState({ messages });
  }
});

// ACTIVE_REPLY_RESET：重置活跃回复相关状态（用于切换到历史消息等场景）
// 注意：这里直接设置状态数组为空，而不是调用 clearActiveCollabStatuses()
// 因为 set(ACTIVE_REPLY_RESET) 会完整替换状态
const ACTIVE_REPLY_RESET = {
  streamingText: null as string | null,
  activeAssistantIntro: null as string | null,
  activeProcessLogs: [] as ProcessLogEntry[],
  activeCollabStatuses: [] as CollabStatusEntry[],
};

export const STREAMING_RESET = {
  ...ACTIVE_REPLY_RESET,
  isLoading: false,
  isThinking: false,
  thinkingSeconds: 0,
  abortController: null as AbortController | null,
  // 🔵 P2: ActiveReplyBuilder 重置（P1-5: 标志位已移除，仅通过 builder.type 判断场景）
  activeReplyBuilder: null as { id: string | null; type: string | null; introText: string | null; text: string | null; searchData: any | null; generationOptions: any | null; mediaData: any | null; metadata: Record<string, any>; warning?: string; pendingMarkdown?: string; } | null,
  // 🔴 P0-2: 重置 lastChunkReceivedAt
  lastChunkReceivedAt: null as number | null,
  // 🔴 F-1: 重置 isFinalizing
  isFinalizing: false,
  // 🔵 重置流式会话结束标志
  streamEnded: false,
};

const SESSION_RESET = {
  ...STREAMING_RESET,
  messages: [] as Message[],
  sessionId: null as string | null,
  currentAgentId: null as string | null,
  currentIntent: 'chat',
  input: '',
  attachments: [] as AttachmentItem[],
  pendingBackendMsgIds: null as { user_msg_id?: string; assistant_msg_id?: string } | null,
  pendingCanvasNodeId: null as string | null,
};

const CLOSE_SESSION_RESET = {
  ...SESSION_RESET,
  projectId: null as string | null,
};

export const useAgentStore = create<AgentState>()((set, get) => {
  const makeSetter = createSetter<AgentState>(set);

  return {
    messages: [],
    currentModelId: 'grsai-gemini-3.1-pro',
    sessionId: null,
    projectId: null,
    skills: [],
    abortController: null,
    availableModels: [],
    searchMode: 'auto',
    agents: [],
    currentAgentId: null,
    defaultAgentId: null,
    dispatcherDegraded: false,
    degradationLevel: 0,
    degradationMessage: '',
    backgroundTasks: [],
    activeCollabStatuses: [],

    isLoading: false,
    isThinking: false,
    thinkingSeconds: 0,
    currentIntent: 'chat',
    input: '',
    streamingText: null,
    activeAssistantIntro: null,
    activeProcessLogs: [],
    attachments: [],
    pendingBackendMsgIds: null,
    pendingCanvasNodeId: null,
    chatHistory: [],
    selectedHistoryId: null,
    showHistoryModal: false,

    // 🔵 P2: ActiveReplyBuilder 初始值（null 表示不在流式回复中，P1-5: 标志位已移除）
    activeReplyBuilder: null,
    // 🔴 P0-2: 初始值
    lastChunkReceivedAt: null,
    // 🔴 F-1: 初始值
    isFinalizing: false,
    // 🔵 流式会话结束标志初始值
    streamEnded: false,

    setCurrentModel: (modelId) => { set({ currentModelId: modelId }); get().saveToLocal(); },
    setSessionId: makeSetter('sessionId'),
    setProjectId: makeSetter('projectId'),
    setSkills: makeSetter('skills'),
    setAbortController: makeSetter('abortController'),
    setMessages: (messagesOrUpdater) => {
      if (typeof messagesOrUpdater === 'function') {
        set((state) => ({ messages: messagesOrUpdater(state.messages) }));
      } else {
        set({ messages: messagesOrUpdater });
      }
      get().saveToLocal();
    },

    setAvailableModels: makeSetter('availableModels'),
    setSearchMode: makeSetter('searchMode'),
    setCurrentAgentId: makeSetter('currentAgentId'),

  loadAgents: async () => {
    try {
      const result = await AgentService.listAgents();
      set({
        agents: result.agents,
        defaultAgentId: result.default,
        currentAgentId: null,
      });
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  },

  ensureSession: async (projectId: string) => {
    const { sessionId } = get();
    if (sessionId) return sessionId;
    try {
      const result = await AgentService.createSession(projectId);
      set({ sessionId: result.sessionId, projectId });
      return result.sessionId;
    } catch (err) {
      console.error('Failed to create session:', err);
      const fallbackId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
      set({ sessionId: fallbackId, projectId });
      return fallbackId;
    }
  },

  restoreSession: async (sessionId: string) => {
    try {
      const result = await AgentService.getSessionMessages(sessionId);
      const { restoreBackendMessage } = await import('@/components/agent/helpers');
      const restoredMessages: Message[] = result.messages.map(restoreBackendMessage).map((m: Message) => ({ ...m, isHistory: true }));
      // 🔵 P1-6: 恢复历史会话时也重置流式状态（builder、标志位、流式文本等）
      // 避免上一个会话的流式状态残留影响新的会话
      set({
        ...STREAMING_RESET,
        messages: restoredMessages,
        sessionId,
        currentAgentId: (result as any).currentAgentId || null,
      });
      get().saveToLocal();
    } catch (err) {
      console.error('Failed to restore session:', err);
    }
  },

  closeSession: async () => {
    const { sessionId } = get();
    if (sessionId) {
      try { await AgentService.closeSession(sessionId); }
      catch (err) { console.error('Failed to close session:', err); }
    }
    set(CLOSE_SESSION_RESET);
  },

  resetSession: () => set(SESSION_RESET),

  initializeSession: async (projectId: string) => {
    const sessionId = await get().ensureSession(projectId);
    const local = await unifiedStorage.getAgent(sessionId);
    if (local) {
      set({
        messages: (local.messages as Message[]).map((m: Message) => ({ ...m, isHistory: true })),
        sessionId, projectId,
        currentModelId: local.currentModelId,
      });
    }
    try { await get().restoreSession(sessionId); }
    catch (err) { console.warn('Backend restore failed, using local cache:', err); }
  },

  saveToLocal: async () => {
    // 🔵 #25: 保存 activeReplyBuilder 到本地缓存，确保跨 Tab 可见
    const { sessionId, projectId, messages, currentModelId, activeReplyBuilder } = get();
    if (sessionId && messages.length > 0) {
      try {
        await unifiedStorage.saveAgent(sessionId, { messages, currentModelId, projectId: projectId || '', activeReplyBuilder });
        notifyOtherTabs(sessionId, messages);
      } catch (e) {
        console.warn('Failed to save to local cache:', e);
      }
    }
  },

  setDegradationStatus: (degraded: boolean, level?: number, message?: string) => {
    set({
      dispatcherDegraded: degraded,
      degradationLevel: level || 0,
      degradationMessage: message || '',
    });
  },

  setBackgroundTasks: makeSetter('backgroundTasks'),

  setActiveCollabStatuses: (setter) => {
    set((state) => {
      if (typeof setter === 'function') {
        return { activeCollabStatuses: setter(state.activeCollabStatuses || []) };
      }
      return { activeCollabStatuses: setter };
    });
  },

  clearActiveCollabStatuses: () => {
    set({ activeCollabStatuses: [] });
  },

  setIsLoading: makeSetter('isLoading'),
  setIsThinking: makeSetter('isThinking'),
  setThinkingSeconds: makeSetter('thinkingSeconds'),
  setCurrentIntent: makeSetter('currentIntent'),
  setInput: makeSetter('input'),
  // 🔴 P0-1 + F-8: 统一流式文本写入入口（单一真源）
  // - builder.type === 'search' / 'generation_confirm' 时: 仅更新 streamingText，不覆盖 builder.text
  // - builder.type === 其他 时: 同步更新 builder.text 和 streamingText
  // - 无 builder: 仅写入 streamingText（向后兼容）
  // 渲染层始终先读 builder.text，回落至 streamingText
  setStreamingText: (text: string | null | ((prev: string | null) => string | null)) => {
    if (typeof text === 'function') {
      set((state) => {
        const resolved = text(state.streamingText);
        const normalized = resolved === '' ? null : resolved;
        if (state.activeReplyBuilder) {
          const btype = state.activeReplyBuilder.type;
          if (btype === 'search' || btype === 'generation_confirm') {
            return { streamingText: normalized };
          }
          return {
            streamingText: normalized,
            activeReplyBuilder: { ...state.activeReplyBuilder, text: normalized },
          };
        }
        return { streamingText: normalized };
      });
    } else {
      set((state) => {
        const normalized = text === '' ? null : text;
        if (state.activeReplyBuilder) {
          const btype = state.activeReplyBuilder.type;
          if (btype === 'search' || btype === 'generation_confirm') {
            return { streamingText: normalized };
          }
          return {
            streamingText: normalized,
            activeReplyBuilder: { ...state.activeReplyBuilder, text: normalized },
          };
        }
        return { streamingText: normalized };
      });
    }
  },
  // 🔴 P0-2: 记录收到 SSE chunk 的时间戳，供 polling 判断 SSE 是否仍活跃
  bumpChunkReceivedAt: () => set({ lastChunkReceivedAt: Date.now() }),
  setActiveAssistantIntro: makeSetter('activeAssistantIntro'),
  setActiveProcessLogs: makeSetter('activeProcessLogs'),
  setAttachments: makeSetter('attachments'),
  setPendingBackendMsgIds: makeSetter('pendingBackendMsgIds'),
  setPendingCanvasNodeId: makeSetter('pendingCanvasNodeId'),
  // 🔵 #21: addPendingLocalMsgId / removePendingLocalMsgId / clearPendingLocalMsgIds 已移除
  setChatHistory: makeSetter('chatHistory'),
  setSelectedHistoryId: makeSetter('selectedHistoryId'),
  setShowHistoryModal: makeSetter('showHistoryModal'),

  // 🔵 P2: ActiveReplyBuilder setter 方法
  // 初始化 builder（在发送消息时调用）
  // 🔴 F-3/F-18: builder.id 在初始化时就确定，避免多路径写入冲突
  // 导入 createLocalId 用于生成唯一 ID
  initActiveReplyBuilder: () => {
    const localId = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    set({
      activeReplyBuilder: {
        id: localId,
        type: null,
        introText: null,
        text: null,
        searchData: null,
        generationOptions: null,
        mediaData: null,
        metadata: {},
        pendingMarkdown: undefined,  // 🔴 P0(v4): markdown 结构缓冲区初始值
      },
    });
  },
  // 更新 builder 的部分字段（不可变更新）
  updateActiveReplyBuilder: (patch: Partial<NonNullable<AgentState['activeReplyBuilder']>>) => {
    set((state) => {
      if (!state.activeReplyBuilder) return state;
      return {
        activeReplyBuilder: { ...state.activeReplyBuilder, ...patch },
      };
    });
  },
  // 获取 builder 内容（同步快照）
  getActiveReplyBuilder: () => {
    const b = get().activeReplyBuilder;
    return b ? { ...b } : null;
  },
  // 清除 builder（完成或 reset 时调用）
  clearActiveReplyBuilder: () => {
    set({ activeReplyBuilder: null });
  },

  resetActiveReplyState: () => set(ACTIVE_REPLY_RESET),

  resetStreamingState: () => {
    const currentSessionId = get().sessionId;
    if (currentSessionId) {
      if (thinkingTimerMap.has(currentSessionId)) {
        clearInterval(thinkingTimerMap.get(currentSessionId)!);
        thinkingTimerMap.delete(currentSessionId);
      }
      thinkingSecondsMap.delete(currentSessionId);
    }
    set({ ...STREAMING_RESET });
  },

  updateActiveProcessLogs: (updater) => {
    set((state) => ({ activeProcessLogs: updater(state.activeProcessLogs) }));
  },
  // 🔴 P2-1: activeProcessLogs 受控操作（统一入口，内部去重 + 状态一致性保证）
  appendActiveProcessLog: (log) => {
    set((state) => {
      // 去重：相同 title + kind 的 active 状态 log 不重复添加
      const exists = state.activeProcessLogs.some(
        (l) => l.title === log.title && l.kind === log.kind && l.status === 'active'
      );
      if (exists) return state;
      return { activeProcessLogs: [...state.activeProcessLogs, log] };
    });
  },
  completeLatestActiveProcessLog: (title, detail) => {
    set((state) => {
      const next = [...state.activeProcessLogs];
      const activeIdx = next.map((l) => l.status).lastIndexOf('active');
      if (activeIdx < 0) return state;
      next[activeIdx] = {
        ...next[activeIdx],
        status: 'completed',
        title: title || next[activeIdx].title,
        detail: detail || next[activeIdx].detail,
      };
      return { activeProcessLogs: next };
    });
  },
  failLatestActiveProcessLog: (title, detail) => {
    set((state) => {
      const next = [...state.activeProcessLogs];
      const activeIdx = next.map((l) => l.status).lastIndexOf('active');
      if (activeIdx < 0) return state;
      next[activeIdx] = {
        ...next[activeIdx],
        status: 'error',
        title: title || next[activeIdx].title,
        detail: detail || next[activeIdx].detail,
      };
      return { activeProcessLogs: next };
    });
  },

  clearThinkingTimer: () => {
    const currentSessionId = get().sessionId;
    if (currentSessionId && thinkingTimerMap.has(currentSessionId)) {
      clearInterval(thinkingTimerMap.get(currentSessionId)!);
      thinkingTimerMap.delete(currentSessionId);
    }
  },

  startThinkingTimer: () => {
    const currentSessionId = get().sessionId;
    if (!currentSessionId) return;

    if (thinkingTimerMap.has(currentSessionId)) {
      clearInterval(thinkingTimerMap.get(currentSessionId)!);
    }
    thinkingTimerMap.set(currentSessionId, setInterval(() => {
      const sid = get().sessionId;
      if (sid && thinkingTimerMap.has(sid)) {
        const currentSeconds = thinkingSecondsMap.get(sid) || 0;
        thinkingSecondsMap.set(sid, currentSeconds + 1);
        set((state) => ({ thinkingSeconds: thinkingSecondsMap.get(sid) || 0 }));
      }
    }, 1000));
  },
  };
});
